import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Select,
  Tag,
  Space,
  Tooltip,
  Modal,
  Empty,
  Spin,
  Typography,
} from "antd";
import {
  SettingOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SwapOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import {
  autoSelectMachines,
  getAvailableMachines,
  updateMachineSelection,
} from "../../api/jobApi";

const { Text } = Typography;
const { Option } = Select;

const MachineSelectionPanel = ({ jobId, selectedMachines, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [machineData, setMachineData] = useState([]);
  const [changeModalVisible, setChangeModalVisible] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [availableMachines, setAvailableMachines] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [selectedNewMachine, setSelectedNewMachine] = useState(null);

  useEffect(() => {
    parseMachineSelection();
  }, [selectedMachines]);

  const parseMachineSelection = () => {
    let parsed = [];
    try {
      if (typeof selectedMachines === "string") {
        parsed = JSON.parse(selectedMachines);
      } else if (Array.isArray(selectedMachines)) {
        parsed = selectedMachines;
      }
    } catch (e) {
      parsed = [];
    }
    // Normalize legacy die_cutting stage to casting
    const normalized = (Array.isArray(parsed) ? parsed : []).map((m) => ({
      ...m,
      stage_code: (m.stage_code === "die_cutting" || m.stage_code === "rotary") ? "casting" : m.stage_code,
      stage_name: (m.stage_code === "die_cutting" || m.stage_name === "Die Cutting / Rotary" || m.stage_name === "Die Cutting") ? "Casting" : m.stage_name,
    }));
    setMachineData(normalized);
  };

  const handleAutoSelect = async () => {
    try {
      setLoading(true);
      const result = await autoSelectMachines(jobId);
      toast.success("Machines auto-selected successfully");
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to auto-select machines");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChangeModal = async (stageRecord) => {
    setSelectedStage(stageRecord);
    setSelectedNewMachine(stageRecord.machine_id);
    setChangeModalVisible(true);
    
    // Fetch available machines for this stage
    try {
      setLoadingAvailable(true);
      const result = await getAvailableMachines(jobId, stageRecord.stage_code, stageRecord.machine_type);
      setAvailableMachines(result.machines || []);
    } catch (error) {
      toast.error("Failed to load available machines");
      setAvailableMachines([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  const handleSaveMachineChange = async () => {
    if (!selectedStage) return;
    
    try {
      setLoading(true);
      await updateMachineSelection(jobId, selectedStage.stage_code, selectedNewMachine, selectedStage.machine_type || null);
      toast.success(`Machine updated for ${selectedStage.stage_name}${selectedStage.machine_type ? ` (${selectedStage.machine_type})` : ""}`);
      setChangeModalVisible(false);
      setSelectedStage(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update machine");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (record) => {
    if (!record.machine_id) {
      return <CloseCircleOutlined style={{ color: "#ff4d4f" }} />;
    }
    if (record.machine_id === "manual" || record.status === "manual") {
      return <ToolOutlined style={{ color: "#1890ff" }} />;
    }
    if (record.is_outsourced) {
      return <SwapOutlined style={{ color: "#722ed1" }} />;
    }
    if (record.status === "warning") {
      return <WarningOutlined style={{ color: "#faad14" }} />;
    }
    return <CheckCircleOutlined style={{ color: "#52c41a" }} />;
  };

  const columns = [
    {
      title: "Stage",
      dataIndex: "stage_name",
      key: "stage_name",
      render: (text, record) => (
        <Space>
          <ToolOutlined />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: "Machine Type",
      dataIndex: "machine_type",
      key: "machine_type",
      render: (text) => (
        <Tag color="blue">{text?.toUpperCase() || "-"}</Tag>
      ),
    },
    {
      title: "Selected Machine",
      dataIndex: "machine_name",
      key: "machine_name",
      render: (text, record) => (
        <Space>
          {getStatusIcon(record)}
          {record.machine_id === "manual" || record.status === "manual" ? (
            <Tag color="blue">Manual Process</Tag>
          ) : record.machine_id ? (
            <>
              <Text>{record.machine_code}</Text>
              <Text type="secondary">({text})</Text>
            </>
          ) : (
            <Text type="danger">Not selected</Text>
          )}
        </Space>
      ),
    },
    {
      title: "Location",
      key: "location",
      render: (_, record) => {
        if (!record.machine_id) return "-";
        if (record.machine_id === "manual" || record.status === "manual") {
          return <Tag color="blue">Manual</Tag>;
        }
        if (record.is_outsourced) {
          return (
            <Tooltip title={record.vendor_name}>
              <Tag color="purple">Outsourced</Tag>
            </Tooltip>
          );
        }
        return <Tag color="green">Inhouse</Tag>;
      },
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<SettingOutlined />}
          onClick={() => handleOpenChangeModal(record)}
        >
          Change
        </Button>
      ),
    },
  ];

  return (
    <>
      <Card
        title={
          <Space>
            <ToolOutlined />
            <span>Machine Selection Plan</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<SyncOutlined spin={loading} />}
            onClick={handleAutoSelect}
            loading={loading}
          >
            Auto-Select Machines
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        {machineData.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No machine selection yet. Click 'Auto-Select Machines' to generate a plan."
          >
            <Button type="primary" onClick={handleAutoSelect} loading={loading}>
              Auto-Select Machines
            </Button>
          </Empty>
        ) : (
          <Table
            dataSource={machineData}
            columns={columns}
            rowKey={(r, idx) => `${r.stage_code}-${r.machine_type || idx}`}
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* Change Machine Modal */}
      <Modal
        title={`Change Machine for ${selectedStage?.stage_name || ""}${selectedStage?.machine_type ? ` (${selectedStage.machine_type})` : ""}`}
        open={changeModalVisible}
        onOk={handleSaveMachineChange}
        onCancel={() => {
          setChangeModalVisible(false);
          setSelectedStage(null);
        }}
        confirmLoading={loading}
        okText="Save"
      >
        {loadingAvailable ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spin />
            <div>Loading available machines...</div>
          </div>
        ) : (
          <>
            <p>Select a machine for <strong>{selectedStage?.stage_name}</strong>:</p>
            <Select
              style={{ width: "100%" }}
              value={selectedNewMachine}
              onChange={setSelectedNewMachine}
              placeholder="Select a machine"
              allowClear
            >
              <Option value={null}>
                <Text type="secondary">-- No machine selected --</Text>
              </Option>
              <Option value="manual">
                <Space>
                  <ToolOutlined style={{ color: "#1890ff" }} />
                  <span>Manual Process (No Machine)</span>
                  <Tag color="blue">Manual</Tag>
                </Space>
              </Option>
              {availableMachines.map((m) => (
                <Option 
                  key={m.id} 
                  value={m.id}
                  disabled={!m.can_process}
                >
                  <Space>
                    {m.can_process ? (
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                    )}
                    <span>{m.code} - {m.name}</span>
                    {!m.is_inhouse && (
                      <Tag color="purple" style={{ marginLeft: 8 }}>
                        Outsource: {m.vendor?.name}
                      </Tag>
                    )}
                    {m.status !== "operational" && (
                      <Tag color="orange">{m.status}</Tag>
                    )}
                  </Space>
                </Option>
              ))}
            </Select>
            {availableMachines.length === 0 && (
              <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                No machines available for this stage type.
              </Text>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default MachineSelectionPanel;
